import { useMemo } from 'react';
import type { User } from '../stores/slices/authSlice';
import type { PermissionCheckOptions, PermissionCheckResult } from '../types/permission';

/**
 * 检查用户是否拥有指定角色
 *
 * @param user - 用户信息
 * @param roles - 需要的角色列表（OR 关系）
 * @returns 是否拥有任一角色
 */
export function checkRoles(user: User | null, roles: string[]): boolean {
  if (!user || !roles || roles.length === 0) {
    return true; // 没有角色要求，默认通过
  }

  const userRoles = user.roles || [];

  // OR 关系：拥有任一角色即可
  return roles.some((role) => userRoles.includes(role));
}

/**
 * 综合权限检查（基于角色）
 *
 * @param user - 用户信息
 * @param options - 权限检查选项
 * @returns 权限检查结果
 */
export function checkPermission(
  user: User | null,
  options: PermissionCheckOptions
): PermissionCheckResult {
  const { roles, customCheck, mode = 'all' } = options;

  const hasRoles = checkRoles(user, roles || []);

  if (customCheck) {
    const customResult = customCheck(user);
    const hasPermission = mode === 'any' ? customResult || hasRoles : customResult && hasRoles;
    const result: PermissionCheckResult = { hasPermission };
    if (!hasPermission && !hasRoles && roles && roles.length > 0) {
      const userRoles = user?.roles || [];
      result.missingRoles = roles.filter((r) => !userRoles.includes(r));
    }
    return result;
  }

  const result: PermissionCheckResult = { hasPermission: hasRoles };
  if (!hasRoles && roles && roles.length > 0) {
    const userRoles = user?.roles || [];
    result.missingRoles = roles.filter((r) => !userRoles.includes(r));
  }
  return result;
}

/**
 * 权限检查 Hook
 *
 * 用于在组件中判断用户是否有权限（基于角色）
 *
 * @example
 * ```tsx
 * const user = useAppSelector((state) => state.auth.user);
 * const { hasPermission } = usePermission(user, {
 *   roles: ['PLATFORM.OWNER'],
 * });
 * ```
 */
export function usePermission(
  user: User | null,
  options: PermissionCheckOptions
): PermissionCheckResult {
  const { roles, customCheck, mode } = options;

  return useMemo(
    () => checkPermission(user, options),
    [user, roles, customCheck, mode]
  );
}

/**
 * 简化版：只检查角色列表
 */
export function useHasRoles(user: User | null, roles: string[]): boolean {
  return useMemo(() => checkRoles(user, roles), [user, roles]);
}

/**
 * 检查用户是否为平台所有者
 * 依据：auth 信息中 spaces[0].isPlatform === true 且 spaces[0].ownerId === 当前用户 id
 */
export function isPlatformOwner(user: User | null): boolean {
  if (!user?.id || !user.spaces?.length) {
    return false;
  }
  const first = user.spaces[0];
  return first.isPlatform === true && first.ownerId === user.id;
}

/**
 * 是否平台所有者 Hook
 */
export function useIsPlatformOwner(user: User | null): boolean {
  return useMemo(() => isPlatformOwner(user), [user]);
}
